import { useState } from 'react';
import type { CardTemplate, Intensity } from '@nudge/shared';
import { useCardsStore } from '../../stores/cards';
import { useCircleStore } from '../../stores/circle';
import { OfflineError } from '../../lib/api';
import { toast } from '../ui/Toast';
import { BottomSheet } from '../ui/BottomSheet';
import { formatError } from '../../utils/formatError';

interface SendSheetProps {
  template: CardTemplate | null;
  onClose: () => void;
}

export function SendSheet({ template, onClose }: SendSheetProps) {
  const [intensity, setIntensity] = useState<Intensity>('soft');
  const [busy, setBusy] = useState(false);
  const sendCard = useCardsStore((s) => s.sendCard);
  const circle = useCircleStore((s) => s.circle);

  async function send() {
    if (!circle || !template) return;
    setBusy(true);
    try {
      await sendCard(circle.id, template.id, intensity);
      toast(intensity === 'firm' ? 'Firm nudge sent' : 'Card sent', 'success');
      onClose();
    } catch (e) {
      if (e instanceof OfflineError) {
        toast('Saved — will send when back online', 'info');
        onClose();
      } else {
        toast(formatError(e, 'Could not send card'), 'error');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <BottomSheet open={!!template} onClose={onClose}>
      {template && (
        <div className="px-6 pb-8 pt-2">
          <div className="flex items-center gap-3">
            <div className="text-4xl" aria-hidden>{template.icon}</div>
            <div>
              <div className="text-lg font-medium text-text-primary">{template.label}</div>
              {template.subtext && <div className="text-sm text-text-muted">{template.subtext}</div>}
            </div>
          </div>

          <fieldset className="mt-6">
            <legend className="text-xs uppercase tracking-wide text-text-muted">Intensity</legend>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setIntensity('soft')}
                className={`rounded-xl border px-4 py-3 text-left ${intensity === 'soft' ? 'border-sage bg-sage/10' : 'border-border-subtle bg-card'}`}>
                <div className="text-sm font-medium text-text-primary">Soft</div>
                <div className="text-xs text-text-muted">Gentle. Badge or quiet.</div>
              </button>
              <button type="button" onClick={() => setIntensity('firm')}
                className={`rounded-xl border px-4 py-3 text-left ${intensity === 'firm' ? 'border-amber bg-amber/10' : 'border-border-subtle bg-card'}`}>
                <div className="text-sm font-medium text-text-primary">Firm</div>
                <div className="text-xs text-text-muted">More urgent. May re-nudge.</div>
              </button>
            </div>
          </fieldset>

          <button onClick={send} disabled={busy}
            className={`mt-6 w-full rounded-xl py-3 text-base font-medium text-white disabled:opacity-50 ${intensity === 'firm' ? 'bg-amber' : 'bg-sage'}`}>
            {busy ? 'Sending…' : intensity === 'firm' ? 'Send firm nudge' : 'Send'}
          </button>
          <button onClick={onClose} className="mt-2 w-full py-2 text-sm text-text-muted">Cancel</button>
        </div>
      )}
    </BottomSheet>
  );
}
